import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useMobile } from '../hooks/useMobile';
import { useReducedMotion } from '../hooks/useReducedMotion';

export function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (isMobile || prefersReduced) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    // Position off-screen initially
    gsap.set([dot, ring], { xPercent: -50, yPercent: -50 });

    const pos = { x: -100, y: -100 };

    const onMouseMove = (e: MouseEvent) => {
      pos.x = e.clientX;
      pos.y = e.clientY;

      // Dot follows cursor tightly
      gsap.to(dot, {
        x: pos.x,
        y: pos.y,
        duration: 0.15,
        ease: 'power2.out',
        overwrite: 'auto',
      });

      // Ring follows with more lag
      gsap.to(ring, {
        x: pos.x,
        y: pos.y,
        duration: 0.5,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };

    const onEnterInteractive = () => {
      gsap.to(ring, {
        scale: 1.8,
        borderColor: 'rgba(52, 211, 153, 0.8)',
        duration: 0.3,
        ease: 'power2.out',
      });
      gsap.to(dot, {
        scale: 0.5,
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const onLeaveInteractive = () => {
      gsap.to(ring, {
        scale: 1,
        borderColor: 'rgba(139, 92, 246, 0.6)',
        duration: 0.3,
        ease: 'power2.out',
      });
      gsap.to(dot, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const attachHoverListeners = () => {
      const targets = document.querySelectorAll('a, button, [data-cursor-hover]');
      targets.forEach((el) => {
        el.addEventListener('mouseenter', onEnterInteractive);
        el.addEventListener('mouseleave', onLeaveInteractive);
      });
      return targets;
    };

    window.addEventListener('mousemove', onMouseMove);

    // Attach after DOM settles
    const timer = setTimeout(attachHoverListeners, 300);

    // Re-attach on DOM changes (debounced to avoid excessive work)
    let mutationTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(attachHoverListeners, 500);
    });
    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      clearTimeout(timer);
      clearTimeout(mutationTimer);
      observer.disconnect();
    };
  }, [isMobile, prefersReduced]);

  if (isMobile || prefersReduced) return null;

  return (
    <>
      {/* Inner dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[99999] h-2 w-2 rounded-full bg-zupa-purple-light"
        style={{ willChange: 'transform' }}
      />
      {/* Outer ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[99998] h-10 w-10 rounded-full"
        style={{
          border: '1.5px solid rgba(139, 92, 246, 0.6)',
          willChange: 'transform',
        }}
      />
    </>
  );
}
